import { FiPlus, FiTrash2 } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { useUnitsAndMeasures } from "newui/store";
import { cn } from "utils/helpers";
import type {
	ScheduleColumn,
	ScheduleRow,
} from "../schedule-of-rates.types";
import {
	COLUMN_TYPE_LABELS,
	formatCurrencyValue,
	SCHEDULE_CELL_BORDER,
} from "../schedule-utils";

export interface ScheduleOfRatesTableViewProps {
	columns: ScheduleColumn[];
	disabled?: boolean;
	hasValidationError?: boolean;
	rows: ScheduleRow[];
	validationErrors?: Set<string>;
	onAddRow?: () => void;
	onCellChange?: (rowId: number, colId: number, value: string) => void;
	onCurrencyBlur?: (rowId: number, colId: number) => void;
	onRemoveRow?: (rowId: number) => void;
}

const ScheduleOfRatesTableView: React.FC<ScheduleOfRatesTableViewProps> = ({
	columns,
	disabled = false,
	hasValidationError = false,
	onAddRow,
	onCellChange,
	onCurrencyBlur,
	onRemoveRow,
	rows,
	validationErrors,
}) => {
	const { values: unitsAndMeasures } = useUnitsAndMeasures();
	const isEditable = !disabled && !!onCellChange;

	if (columns.length === 0) {
		return null;
	}

	const renderCell = (row: ScheduleRow, col: ScheduleColumn, rowIdx: number) => {
		const value = row.values[col.id] ?? "";
		const hasError = validationErrors?.has(`${row.id}-${col.id}`) ?? false;
		const errorBorder = hasError ? "border-red-500" : "border-gray-300";

		if (disabled) {
			let displayValue = value || "-";
			if (col.columnType === "currency" && value) {
				displayValue = `$${formatCurrencyValue(value)}`;
			}
			if (col.columnType === "unit_of_measurement" && value) {
				displayValue =
					unitsAndMeasures?.find((u) => u.value === Number(value))?.label ??
					value;
			}
			return (
				<span
					className="text-gray-900"
					data-testid={`cell-view-${row.id}-${col.id}`}
				>
					{displayValue}
				</span>
			);
		}

		if (col.columnType === "unit_of_measurement") {
			return (
				<select
					className={cn(
						"w-full rounded border bg-white px-2 py-1 text-sm",
						errorBorder
					)}
					value={value}
					onChange={(e) => onCellChange?.(row.id, col.id, e.target.value)}
					aria-label={`${col.label} for row ${rowIdx + 1}`}
					data-testid={`cell-${row.id}-${col.id}`}
				>
					<option value="">Select unit</option>
					{unitsAndMeasures?.map((unit) => (
						<option key={unit.value} value={unit.value}>
							{unit.label}
						</option>
					))}
				</select>
			);
		}

		const inputType =
			col.columnType === "number" || col.columnType === "currency"
				? "number"
				: "text";

		return (
			<input
				type={inputType}
				className={cn(
					"w-full rounded border bg-white px-2 py-1 text-sm",
					errorBorder
				)}
				value={value}
				onChange={(e) => onCellChange?.(row.id, col.id, e.target.value)}
				aria-label={`${col.label} for row ${rowIdx + 1}`}
				onBlur={() => {
					if (col.columnType === "currency" && value) {
						onCurrencyBlur?.(row.id, col.id);
					}
				}}
				placeholder={col.columnType === "currency" ? "0.00" : ""}
				min={
					col.columnType === "number" || col.columnType === "currency"
						? 0
						: undefined
				}
				step={1}
				data-testid={`cell-${row.id}-${col.id}`}
			/>
		);
	};

	return (
		<div
			className={cn(
				"overflow-x-auto rounded-xl border bg-white shadow-sm",
				hasValidationError ? "border-2 border-red-500" : "border-[#EAECF0]"
			)}
			data-testid="schedule-table-view"
		>
			<table className="w-full table-auto" data-testid="schedule-table">
				<caption className="sr-only">Schedule of Rates</caption>
				<thead>
					<tr className="bg-[#F9FAFB]">
						<th
							className={cn(
								SCHEDULE_CELL_BORDER,
								"w-10 px-2 py-2 text-center font-roboto text-xs font-medium text-tertiary-600"
							)}
						>
							#
						</th>
						{columns.map((col) => (
							<th
								key={col.id}
								className={cn(
									SCHEDULE_CELL_BORDER,
									"px-2 py-2 font-roboto text-xs font-medium text-tertiary-600"
								)}
								style={{ minWidth: "120px" }}
							>
								<div className="flex items-center gap-1">
									<span>{col.label}</span>
									<span className="text-[10px] text-gray-400">
										({COLUMN_TYPE_LABELS[col.columnType]})
									</span>
								</div>
							</th>
						))}
						{isEditable && onRemoveRow && (
							<th className="w-10 bg-[#F9FAFB]" />
						)}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, idx) => (
						<tr key={row.id} className="border-b border-[#EAECF0]">
							<td
								className={cn(
									SCHEDULE_CELL_BORDER,
									"text-center font-roboto text-xs text-primary-500 bg-gray-50"
								)}
							>
								{idx + 1}
							</td>
							{columns.map((col) => (
								<td
									key={col.id}
									className={cn(SCHEDULE_CELL_BORDER, "px-1 py-1")}
								>
									{renderCell(row, col, idx)}
								</td>
							))}
							{isEditable && onRemoveRow && (
								<td className="px-1">
									<button
										type="button"
										className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
										onClick={() => onRemoveRow(row.id)}
										title="Remove row"
										aria-label={`Remove row ${idx + 1}`}
										data-testid={`remove-row-${row.id}`}
									>
										<FiTrash2 size={12} />
									</button>
								</td>
							)}
						</tr>
					))}
				</tbody>
			</table>

			{isEditable && onAddRow && (
				<div className="flex items-center gap-2 border-t border-[#EAECF0] px-4 py-2">
					<Button
						size="xs"
						btntype="secondary"
						onClick={onAddRow}
						data-testid="add-row-btn"
					>
						<FiPlus className="mr-1" />
						Add Row
					</Button>
				</div>
			)}
		</div>
	);
};

export { ScheduleOfRatesTableView as ScheduleTableView };
